/**
 * Shared Game Pass API types for the PAM frontend.
 *
 * Currently scopes to the Seasons listing endpoint
 * (`GET /game-pass-admin/v2/admin/seasons`). When BE ships richer
 * Seasons-related endpoints (per-season detail, create/update, etc.),
 * their request / response shapes belong here as well so every
 * consumer reads from a single source of truth — same convention as
 * `src/types/affiliates.ts` and `src/types/staff.ts`.
 *
 * `SeasonApiRecord` mirrors the live BE response verbatim. Only the
 * fields the listing mapper actually reads (`id` / `season_id`,
 * `name`, `display_name`, `status`, `start_date`, `end_date`,
 * `challenge_count`, `task_count`) are required-shaped; everything
 * else is optional / nullable to match the real payload so consumers
 * never have to guess what may be absent. The mapper in
 * `src/queries/game-pass/seasonsMappers.ts` is the only listing
 * consumer that touches these names — adjusting them there
 * propagates to every card without touching the page component.
 */

/** Canonical lifecycle status enum the backend uses on the `?status=`
 *  query param and in every season record. Defined as a `const … as
 *  const` object (rather than a TS `enum`) so it:
 *    - matches the repo's existing convention (e.g. `ENTITY_TYPES`,
 *      `BET_STATUS`),
 *    - tree-shakes cleanly,
 *    - and produces a string-literal union type — letting `SeasonApiStatus`
 *      stay assignment-compatible with raw string literals so existing
 *      consumers don't need to migrate in lockstep.
 *
 *  Every mapper / partitioner / hook in this module references these
 *  named constants instead of the raw string values, so a typo
 *  (`"cancelled"` vs `"canceled"`, anyone?) becomes a compile error
 *  instead of a silent runtime bug. */
export const SEASON_API_STATUS = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  LOCKED: "locked",
  ACTIVE: "active",
  GRACE: "grace",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

/** String-literal union derived from the canonical
 *  `SEASON_API_STATUS` object. Mirrors the bullet list shared in the
 *  API spec; keep `SEASON_API_STATUS` in lockstep with the BE enum so
 *  the listing page's per-status partitioning stays exhaustive. */
export type SeasonApiStatus =
  (typeof SEASON_API_STATUS)[keyof typeof SEASON_API_STATUS];

/** Allowed values for the BE `task_submission_mode` enum. Modeled as
 *  a string-literal union (rather than `string`) so a typo in a
 *  hand-written fixture surfaces at compile time. */
export type SeasonTaskSubmissionMode = "AUTO" | "MANUAL";

/** Whether challenges within a season must be completed in order. */
export type SeasonProgressionMode = "SEQUENTIAL" | "ANY_ORDER";

/** Allowed values for the BE `cancellation_policy` enum on the
 *  seasons listing. Per the V2 Game Pass API guide the partial-refund
 *  policy is `"PRORATED"` (not `"PARTIAL"`); widening this union is a
 *  deliberate BE-contract change rather than something the mapper has
 *  to guess at. */
export type SeasonCancellationPolicy = "NO_REWARDS" | "PRORATED" | "FULL";

/** Per-season record returned in the `seasons` array of
 *  `GET /game-pass-admin/v2/admin/seasons`. Field names mirror the BE
 *  payload exactly — any rename here is a coordinated BE+FE change.
 *
 *  Required fields (`name`, `status`, `start_date`, `end_date`) are
 *  the ones the listing card reads to render its title, badge,
 *  countdown, and date range; everything else (counts, lifecycle
 *  timestamps, configuration flags) is optional / nullable because
 *  BE legitimately omits or nulls them depending on the season's
 *  lifecycle stage (a `draft` has no `activated_at`, a `scheduled`
 *  season has no `cancelled_at`, etc.). The mapper falls back to
 *  safe defaults so partial payloads never break the page. */
export interface SeasonApiRecord {
  /** Unique id used as the React key and as the `:seasonId` route
   *  param for the Season Details page. Sent as `id` by the live BE;
   *  the mapper also accepts the legacy `season_id` alias so a future
   *  BE rename costs zero consumer changes. */
  id?: string;
  /** Legacy id alias. Kept on the type until BE confirms it's
   *  permanently retired. */
  season_id?: string;
  /** Operator the season belongs to. Not rendered on the listing
   *  card today but surfaced through the type so future per-operator
   *  filters / analytics can read it without re-shaping the record. */
  operator_id?: string;
  /** Internal / canonical name (e.g. `"June 2026 Season"`). Always
   *  present on a real BE record. */
  name: string;
  /** Admin-supplied user-facing override. When non-null the mapper
   *  prefers this over `name` for the card title; when null the card
   *  falls back to `name`. */
  display_name?: string | null;
  /** BE-side slug (`"june_2026"`). Useful for future deep-links /
   *  query-string identifiers; not rendered on the card. */
  internal_name?: string | null;
  /** Admin-supplied free-text description shown in the Edit / View Season
   *  modal's General section. Optional + nullable since older records (and
   *  slim listing responses) may omit it. */
  description?: string | null;
  /** S3 object key of the season banner image (see `uploadGamePassImage`).
   *  Omitted when the admin didn't attach a banner. */
  img_key?: string | null;
  /** Fully-qualified preview URL for the season banner, taken verbatim from
   *  the BE `image_url` field. Preferred over rebuilding a URL from `img_key`. */
  image_url?: string | null;
  status: SeasonApiStatus;
  /** ISO-8601 date or date-time at which the season opens. */
  start_date: string;
  /** ISO-8601 date or date-time at which the season closes. */
  end_date: string;
  /** Total number of challenges configured on the season. Drives the
   *  "X Challenges" metric on the season card. BE name is singular
   *  (`challenge_count`, not `challenges_count`). */
  challenge_count?: number;
  /** Total number of tasks across every challenge. Drives the
   *  "Y Tasks" metric on the season card. BE name is singular
   *  (`task_count`, not `tasks_count`). */
  task_count?: number;
  /** ─── Audit timestamps (ISO-8601 strings) ────────────────────── */
  created_at?: string;
  updated_at?: string;
  /** ─── Lifecycle timestamps (nullable until the matching
   *      transition has happened) ─────────────────────────────── */
  scheduled_at?: string | null;
  activated_at?: string | null;
  locked_at?: string | null;
  grace_started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  /** ─── Free-/premium-path configuration ───────────────────────── */
  free_path_enabled?: boolean;
  premium_path_enabled?: boolean;
  premium_price_gc?: number | null;
  premium_price_sc?: number | null;
  /** ─── Behavioural toggles ────────────────────────────────────── */
  lock_before_minutes?: number;
  grace_period_minutes?: number;
  task_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all sportsbook tasks in this season. */
  sportsbook_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all casino tasks in this season. */
  casino_submission_mode?: SeasonTaskSubmissionMode;
  /** Whether users must complete challenges in sort order (`SEQUENTIAL`) or
   *  may complete them in any order (`ANY_ORDER`). */
  progression_mode?: SeasonProgressionMode;
  enrollment_required?: boolean;
  retroactive_enrollment?: boolean;
  visible_before_active?: boolean;
  auto_claim_rewards?: boolean;
  cancellation_policy?: SeasonCancellationPolicy;
  /** ─── Cadence / cloning / triggering metadata ────────────────── */
  cadence_type?: string | null;
  cloned_from_season_id?: string | null;
  trigger_season_id?: string | null;
  /** ─── Reason copy (set only when the matching transition has
   *      happened) ───────────────────────────────────────────────── */
  completed_reason?: string | null;
  cancellation_reason?: string | null;
  /** Snapshot of the season's full configuration at the moment it
   *  was scheduled / activated. Opaque from the listing's POV — kept
   *  as `unknown` so consumers must narrow explicitly before reading. */
  configuration_snapshot?: unknown;
}

/** Top-level success envelope for `GET /game-pass-admin/v2/admin/seasons`.
 *  Note this is NOT the standard `{ success, message, data }` shape —
 *  the seasons array is co-located with the success flag at the
 *  top level. */
export interface SeasonsListResponse {
  success: boolean;
  seasons: SeasonApiRecord[];
}

/** Query params accepted by the listing endpoint. `status` is the
 *  only documented filter today. */
export interface SeasonsListQuery {
  status?: SeasonApiStatus;
}

/** A single Game Pass currency entry from `GET /config`. The platform
 *  denominates rewards / bets in two currencies — the play-money balance
 *  (`play`) and the redeemable value balance (`value`). `name` is the
 *  admin-facing display label (e.g. "Rebet Coin"); `money_type` is the
 *  canonical lowercase wire value used everywhere else (`rebet_coin` /
 *  `rebet_cash`). `min_bet` is the sportsbook minimum stake the operator
 *  enforces for this currency (e.g. 100 coins) — the Create Task "Minimum Bet
 *  Amount" must be at least this value when the currency is selected. */
export interface GamePassCurrency {
  name: string;
  money_type: string;
  min_bet?: number;
}

/** A single reward-type entry from `GET /config`. The backend expresses the
 *  challenge reward options as `currency` × `tier` combinations, each with a
 *  display `label`. The UI's challenge reward dropdown collapses these to the
 *  distinct currencies (the tier is chosen by the challenge's Free/Premium
 *  tab, not the reward row). */
export interface GamePassRewardType {
  /** `play` (Rebet Coin) or `value` (Rebet Cash). */
  currency: ChallengeRewardCurrency;
  /** `free` or `premium` — the audience the reward applies to. */
  tier: ChallengeRewardTier;
  /** Admin-facing label, e.g. "Rebet Coin (Free path)". */
  label: string;
}

/** A single tunable parameter for a bonus reward
 *  (`config.bonus_reward_types[*].params`). Rendered as an input when the bonus
 *  is selected in the challenge reward dropdown: `required` params start blank
 *  and must be filled; optional params are prefilled with `default` and stay
 *  editable. `type` (`integer` / `number` / `string` / `array`) drives the
 *  input's keyboard/sanitisation. */
export interface GamePassBonusRewardParam {
  name: string;
  type: string;
  required: boolean;
  default?: number | string | null;
  description?: string;
}

/** A single bonus-reward entry from `GET /config` (`bonus_reward_types`, e.g.
 *  Free Cash Pick, Profit Boost, Deposit Match). These sit alongside the
 *  currency `reward_types` in the challenge reward dropdown. `reward_type` is
 *  the wire value the BE grants for the bonus (`null` while a bonus is defined
 *  but not yet wired — those carry `available: false` and are hidden from the
 *  picker); `params` are the per-bonus tunables surfaced as inputs. */
export interface GamePassBonusRewardType {
  /** Bonus category, e.g. `free_pick`, `profit_boost`, `deposit_match`. Shipped
   *  as the reward's `currency` on the wire. */
  currency: string;
  /** Admin-facing label, e.g. "Free Cash Pick". */
  label: string;
  /** BE grant identifier, e.g. `free_cash_pick`; `null` when unavailable. */
  reward_type: string | null;
  /** Whether the bonus is live — `false` bonuses are hidden from the picker. */
  available: boolean;
  /** Label for the reward's amount input, e.g. "Boost percentage". */
  amount_label?: string;
  params?: GamePassBonusRewardParam[];
}

/** Response from `GET /gamepass/v2/admin/config`.
 *
 * The create-season flow reads `operator_id` from here instead of a
 * localStorage override so every draft is created against the backend's
 * current operator context. `currencies` drives the task "Bet Currency"
 * dropdown and the challenge reward dropdown; `reward_types` enumerates the
 * currency/tier reward combinations the operator supports. */
export interface GamePassConfigResponse {
  operator_id: string;
  currencies?: {
    play?: GamePassCurrency;
    value?: GamePassCurrency;
  };
  reward_types?: GamePassRewardType[];
  /** Bonus rewards (Free Cash Pick, Profit Boost, …). Merged into the challenge
   *  reward dropdown alongside `reward_types`; either list may be absent and the
   *  other still populates the picker. */
  bonus_reward_types?: GamePassBonusRewardType[];
}

// ─── Task Options API ──────────────────────────────────────────────────────
//
// `GET /game-pass-admin/v2/admin/task-options` returns the catalogue the
// Create Task / Create Challenge surfaces render their dropdowns from:
// sports, leagues, the BE enums (bet types, outcomes, data sources, paths,
// …), the rule-field/operator metadata, social event types, and casino
// providers. Fields the UI doesn't currently surface are still typed so a
// future consumer reads from a single source of truth.

/** A single sport entry. `value` is what ships in the task's `sport_id`
 *  rule, so it round-trips straight back into the Sport Specific picker. */
export interface TaskOptionSport {
  id: string;
  name: string;
  value: string;
}

/** A single league (tournament) entry under a sport. Display text is
 *  `label` when present (`"Name (Country)"`), otherwise `name`. When
 *  present, `tournament_id` is the value stored on `trigger.sportLeagues`
 *  and shipped in the task's `league_id` rule; when absent the mapper
 *  falls back to `name`. */
export interface TaskOptionLeague {
  /** BE tournament / league id (e.g. `sr:tournament:17`). Omitted or null on
   *  partial task-options payloads — consumers fall back to `name`. */
  tournament_id?: string | null;
  name: string;
  /** Legacy alias echoed by some task-options payloads; not sent on the wire. */
  value?: string;
  abbreviations?: string[];
  /** Readable country (e.g. `England`). Empty string when the source has none. */
  country?: string;
  /** Picker copy from BE — `"Name (Country)"`, falling back to `name`. */
  label?: string;
  /** Sport slug echoed on the league row (e.g. `soccer`). */
  sport?: string;
}

/** The `data` payload of `GET /task-options`. Every field is optional so a
 *  partial / evolving payload never breaks the consumers — each derivation
 *  helper falls back to its static catalogue when a field is absent. */
export interface TaskOptionsData {
  sports?: TaskOptionSport[];
  /** Leagues keyed by sport `name` (e.g. `soccer`, `ice_hockey`). Some keys
   *  arrive hyphenated (`cross-country`, `esport_counter-strike`) where the
   *  matching `sports[].name` is underscored — consumers normalise before
   *  matching. */
  leagues?: Record<string, TaskOptionLeague[]>;
  data_sources?: string[];
  completion_modes?: string[];
  bet_types?: string[];
  outcomes?: string[];
  paths?: string[];
  counting_methods?: string[];
  rule_fields?: Record<string, string[]>;
  rule_operators?: string[];
  social_event_types?: string[];
  casino_providers?: string[];
}

/** Top-level envelope for `GET /game-pass-admin/v2/admin/task-options`. Uses
 *  the standard `{ success, data }` shape (unlike the seasons / challenges /
 *  tasks listings, which co-locate the array with the success flag). */
export interface TaskOptionsResponse {
  success: boolean;
  data: TaskOptionsData;
}

// ─── Casino games catalogue API ────────────────────────────────────────────
//
// Powers the Create Task casino provider/game picker (`data_source = CASINO`).
// `GET /game-pass-admin/v2/admin/casino-games/providers` lists the providers;
// `GET /game-pass-admin/v2/admin/casino-games` lists / searches the games,
// filtered by `provider` or `search` and paginated via `limit` / `offset`.

/** A casino provider summary row. `name` is the wire id shipped verbatim as
 *  the task's `provider` rule (and used to filter the games endpoint). `total`
 *  is the provider's game count; `active` is how many are currently enabled. */
export interface CasinoGamesProvider {
  name: string;
  total: number;
  active: number;
}

/** Envelope for `GET /casino-games/providers`. */
export interface CasinoProvidersResponse {
  providers: CasinoGamesProvider[];
  total_games: number;
}

/** A single casino game. `game_id` is shipped verbatim as the task's `game_id`
 *  rule, so it round-trips back into the picker. `category` is free-text and
 *  provider-dependent (often empty) — used only for visual grouping. */
export interface CasinoGame {
  game_id: string;
  name: string;
  provider: string;
  category: string;
  is_active: boolean;
  /** Every id alias echoed on the catalogue row (`game_id`, numeric `id`, …).
   *  Used to match tasks that stored a legacy id while the picker lists another. */
  match_ids?: string[];
}

/** Envelope for `GET /casino-games` (provider list / search). `filtered_total`
 *  is the count matching the active filter (drives "Show More" pagination);
 *  `unfiltered_total` is the full catalogue size. */
export interface CasinoGamesResponse {
  games: CasinoGame[];
  filtered_total: number;
  unfiltered_total: number;
  limit: number;
  offset: number;
}

/** Minimal payload the Step 1 wizard currently collects for draft creation.
 * `operator_id` is appended in the query layer from `GET /config` because the
 * backend still requires it on create even though the form does not expose it. */
export interface CreateSeasonRequest {
  name: string;
  /** Admin-supplied free-text description. Only sent when the admin entered
   *  one (omitted for a blank field). */
  description?: string;
  start_date: string;
  end_date: string;
  free_path_enabled: boolean;
  premium_path_enabled: boolean;
  /** @deprecated Legacy season-wide mode — prefer `sportsbook_submission_mode`. */
  task_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all sportsbook tasks in this season. */
  sportsbook_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all casino tasks in this season. */
  casino_submission_mode?: SeasonTaskSubmissionMode;
  /** Whether users must complete challenges in sort order. Defaults to
   *  `SEQUENTIAL` when omitted on create. */
  progression_mode?: SeasonProgressionMode;
  /** Premium-tier price amount in gold coins. Only sent when the premium tier
   *  is enabled and the admin entered a valid positive amount; `null`
   *  otherwise (premium disabled / blank / invalid input). */
  premium_price_gc?: number | null;
  /** Premium-tier price amount in sweeps coins. Same contract as
   *  `premium_price_gc`, but optional even when the premium tier is enabled. */
  premium_price_sc?: number | null;
  /** S3 object key of the uploaded season banner (see `uploadGamePassImage`).
   *  Omitted when the admin didn't attach a banner. */
  img_key?: string;
}

/** Draft season returned from `POST /game-pass-admin/v2/admin/seasons`.
 *  Unlike the listing response, early drafts may not have schedule fields
 *  populated yet, so `start_date` / `end_date` are nullable/optional here. */
export interface CreatedSeasonRecord
  extends Omit<SeasonApiRecord, "start_date" | "end_date"> {
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateSeasonResponse {
  success: boolean;
  season?: CreatedSeasonRecord;
  data?: CreatedSeasonRecord;
}

/** Partial body for `PUT /game-pass-admin/v2/admin/seasons/{season_id}`.
 *
 * The edit modal only authors a subset of season fields today, so the update
 * hook ships a sparse payload and omits anything untouched. */
export interface UpdateSeasonRequest {
  name?: string;
  /** Admin-supplied free-text description. Sent (including as an empty
   *  string to clear it) only when changed from the stored value. */
  description?: string;
  start_date?: string;
  end_date?: string;
  free_path_enabled?: boolean;
  premium_path_enabled?: boolean;
  /** @deprecated Legacy season-wide mode — prefer per-source submission modes. */
  task_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all sportsbook tasks in this season. */
  sportsbook_submission_mode?: SeasonTaskSubmissionMode;
  /** Default submission mode for all casino tasks in this season. */
  casino_submission_mode?: SeasonTaskSubmissionMode;
  /** Whether users must complete challenges in sort order. */
  progression_mode?: SeasonProgressionMode;
  /** Predecessor link for auto-start: set to the active season's id when
   *  auto-start is enabled, or `null` to clear an existing link when the admin
   *  turns auto-start off. Only sent when it changes from the stored value. */
  trigger_season_id?: string | null;
  /** Season banner image. `undefined` omits the field (edit didn't touch the
   *  image); `null` clears a persisted banner; a string replaces it. */
  img_key?: string | null;
}

export interface UpdateSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface ScheduleSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface ActivateSeasonRequest {
  force?: boolean;
}

export interface ActivateSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface UnscheduleSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

/** Envelope for `POST /game-pass-admin/v2/admin/seasons/{season_id}/unlock`.
 *  Unlocking a `locked` ("Up Next") season transitions it back to
 *  `scheduled` and re-opens it for editing; the BE echoes the updated
 *  season record so the listing / detail caches can refresh in place —
 *  mirroring the activate / cancel / unschedule envelopes. */
export interface UnlockSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface CancelSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface EndSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

/** Audit fields the admin fills in the End Season confirmation modal
 *  (FE-2247); forwarded with the end-season request for the audit trail. */
export interface EndSeasonAudit {
  firstName: string;
  lastName: string;
  /** Native date-input value, `YYYY-MM-DD`. */
  takedownDate: string;
}

export interface DeleteSeasonResponse {
  success: boolean;
}

/** Envelope for `POST /game-pass-admin/v2/admin/seasons/{season_id}/clone`.
 *  Cloning copies a season (and its challenges / tasks) into a new draft;
 *  the BE echoes the newly created season record so the listing cache can
 *  refresh and surface the copy in the Drafts column. Mirrors the
 *  activate / cancel envelopes — the record may arrive under `season` or
 *  the generic `data` key. */
export interface CloneSeasonResponse {
  success: boolean;
  season?: SeasonApiRecord;
  data?: SeasonApiRecord;
}

export interface ActiveSeasonEnrollment {
  path: string;
  enrolled_at: string;
}

export interface ActiveSeasonResponse {
  success: boolean;
  id: string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  start_date: string;
  end_date: string;
  status: SeasonApiStatus;
  premium_path_enabled?: boolean;
  premium_price_gc?: number | null;
  premium_price_sc?: number | null;
  image_url?: string | null;
  enrolled?: boolean;
  enrollment?: ActiveSeasonEnrollment;
}

// ─── User Progress API ─────────────────────────────────────────────────────
//
// `GET /game-pass/v2/admin/users/{user_id}/progress?season_id=…` returns the
// current Game Pass enrollment plus the per-challenge / per-task completion
// state for that season from the user-manager surface.

export interface GamePassUserEnrollment {
  current_challenge_id: string;
  user_id: string;
  current_challenge_index: number;
  is_premium: boolean;
  current_challenge: string;
  username: string;
  enrolled_at: number | string;
  /** When present, preferred over `enrolled_at` for premium purchase copy. */
  premium_purchased_at?: number | string | null;
  season_id: string;
}

export interface GamePassUserRedeemedBet {
  amount?: number;
  bet_id?: string;
  bet_type?: string;
  category?: string;
  combined_odds?: string;
  created_at?: string | number;
  finalized?: string;
  game_id?: string;
  game_name?: string | null;
  /** Alias some casino payloads use instead of `game_name`. */
  game_title?: string | null;
  game_type?: string;
  /** Net winnings (`stake × (odds − 1)` on a win, `"0"` on a loss, `null` pending). */
  win_amount?: number | string | null;
  wager_type?: string;
  is_parlay?: boolean;
  money_type?: string;
  num_legs?: number;
  odds?: string;
  operator_id?: number;
  /** Total return (`stake × odds` on a win). Distinct from `win_amount`. */
  payout?: number | string | null;
  provider?: string;
  redeemed?: boolean;
  /** Unix seconds as an int string (same shape as `created_at`). */
  settled_at?: string | number | null;
  source?: string;
  sub?: string | null;
  user_id?: string;
  /** Username of the rebetter (the user who re-bet the owner's pick), when the
   *  BE resolves it on the redeemed bet — falls back to `user_id` (FE-2579). */
  username?: string;
  source_user_id?: string;
  source_username?: string;
  wager?: number | string;
  wager_amount?: number | string;
  won?: string;
}

export interface GamePassUserProgressTask {
  completed_at: number | null;
  redeemed_bet_ids?: string[];
  count?: number;
  /** Goal for the counter ("count / target_value"); null when unset. */
  target_value?: number | null;
  index?: number;
  task_id?: string;
  name?: string;
  task_name?: string;
  description?: string;
  bet_id?: string;
  completed: boolean;
  reason?: string;
  completed_reason?: string;
  manual_pass_reason?: string;
  manually_passed?: boolean;
  challenge_order?: number;
  data_source?: string;
  /** BE-classified challenge type (FE-2579), e.g. `"REBET"`. Primary
   *  discriminator for the history table; see {@link CHALLENGE_TYPE}. */
  challenge_type?: ChallengeType;
  xp_reward?: number;
  streak_count?: number;
  /** Monotonic high-water consecutive run. Unlocks STREAK tier rungs; stays
   *  after a break. Distinct from `streak_count` (the live run). */
  streak_best?: number;
  streak_last_date?: string | null;
  /** First missed calendar day that ended the previous login streak (FE-2647).
   *  `null` when the current streak is unbroken. */
  streak_broken_on?: string | null;
  /** Unique calendar days the user logged in (first login per day). */
  login_days?: GamePassLoginDay[];
  /** Forensic streak days from the PAM progress read when the BE inlines them
   *  (same shape as streak-history `days`). */
  days?: GamePassStreakHistoryDay[];
  /** First credited login of the month (PAM progress, support-only). */
  first_login_at?: string | number | null;
  /** Epoch (s) of the most recent counted login. */
  last_login_at?: string | number | null;
  /** `sign_in` | `game_pass_open` | `enrollment` — PAM progress, support-only. */
  first_login_source?: string | null;
  last_login_source?: string | null;
  /** Count of broken streaks this month. Optional; forensic `days[].status
   *  === "broken"` is preferred. Not derived from social-event history. */
  streaks_broken?: number;
  /** Container daily boundary as a UTC hour (e.g. 14 = 9 AM EST). */
  streak_reset_hour?: number | string | null;
  /** Preformatted countdown leftover from older payloads. Prefer `days[].window_end`. */
  window_time_left?: string;
  time_left_in_window?: string;
  /** Instant the current login window closes. Prefer `days[].window_end`. */
  window_closes_at?: string | number | null;
  /** Epoch (s) of the next boundary after the response. Alias of `window_closes_at`. */
  window_ends_at?: string | number | null;
  /** Authored / progress-echoed ladder. STREAK tasks may set `unlocked`. */
  tiers?: ReadonlyArray<CampaignTaskTier>;
  event_type?: string;
  redeemed_bets?: GamePassUserRedeemedBet[];
  /** Admin progress ships this on the task when the user has claimed the reward. */
  reward_claimed?: boolean;
  /** Optional per-tier claim rows when the payload includes them. */
  tier_claims?: Array<{
    claimed?: boolean;
    reward_claimed?: boolean;
  }>;
  /** DISTINCT_COUNT keys (usually the rebetter user id) on REBET / FOLLOWER /
   *  REFERRAL tasks. */
  distinct_keys?: string[];
  /** Resolved users for those keys — REBET history uses `username` as
   *  "Username of Rebetter" (FE-2579 live progress contract). */
  distinct_users?: GamePassProgressDistinctUser[];
}

/** One unique login calendar day on a LOGIN / Login Streak monthly task
 *  (FE-2647). Extra logins the same day are omitted. */
export interface GamePassLoginDay {
  date?: string;
  first_login_at?: string | number;
  username?: string;
  /** Consecutive days ending on this date. */
  streak?: number;
  consecutive_day?: number;
  /** First missed calendar day if this login restarted a streak. */
  streak_broken_on?: string | null;
  login_type?: string;
  type?: string;
  source?: string | null;
  occurred_at?: string | number | null;
  counts_toward?: string;
  window_label?: string;
  window_start?: string | null;
  window_end?: string | null;
  time_left_in_window?: string;
  window_closed_at?: string | number | null;
  status?: GamePassStreakHistoryDayStatus | string;
  reward_hit?: string;
}

/** `GET …/tasks/{task_id}/streak-history` day row (FE-2664). Oldest-first. */
export type GamePassStreakHistoryDayStatus =
  | "first_login"
  | "continued"
  | "restarted"
  | "broken";

export type GamePassStreakHistorySource =
  | "sign_in"
  | "game_pass_open"
  | "enrollment";

export interface GamePassStreakHistoryDay {
  date?: string;
  /** Real login time; `null` on a break row. */
  occurred_at?: string | number | null;
  /** `sign_in` | `game_pass_open` | `enrollment`; `null` on a break row.
   *  `enrollment` is a day-1 credit from joining, not a real login. */
  source?: GamePassStreakHistorySource | string | null;
  /** Running consecutive count that day; `0` on a break row. */
  consecutive_day?: number;
  /** Reset-hour-shifted local-day window (UTC). */
  window_start?: string | null;
  window_end?: string | null;
  status?: GamePassStreakHistoryDayStatus | string;
}

/** Inner `data` on `GET …/streak-history`. */
export interface GamePassStreakHistoryPayload {
  task_id?: string;
  counting_method?: string;
  /** UTC hour 0–23 the login window resets. Same key as campaign/task. */
  streak_reset_hour?: number | string | null;
  current_streak?: number;
  best_streak?: number;
  last_counted_date?: string | null;
  counted_dates?: string[];
  days?: GamePassStreakHistoryDay[];
  derived_best_streak?: number;
  as_of?: string;
}

export interface GamePassStreakHistoryResponse {
  success?: boolean;
  days?: GamePassStreakHistoryDay[];
  streak_reset_hour?: number | string | null;
  /** Log-derived longest run — cross-check only, never a tier-unlock signal. */
  derived_best_streak?: number;
  data?: GamePassStreakHistoryDay[] | GamePassStreakHistoryPayload;
}

export interface GamePassProgressDistinctUser {
  user_id?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  profile_pic_link?: string;
  /** Referral: when the referred user signed up (Ahmad / FE-2579). */
  referred_at?: string | number | null;
  /** Referral: qualifying deposit completed (boolean or `{ completed, at }`). */
  qualifying_deposit?:
    | boolean
    | number
    | string
    | {
        completed?: boolean;
        value?: boolean;
        at?: string | number | null;
        completed_at?: string | number | null;
        timestamp?: string | number | null;
      }
    | null;
  first_purchase?: boolean | number | string | null;
  first_purchase_at?: string | number | null;
  /** Follower: first follow timestamp (Ahmad / FE-2579). */
  first_follow_at?: string | number | null;
  counted?: boolean;
}

export interface GamePassUserChallengeProgress {
  completed: boolean;
  completed_at: number | null;
  version?: number;
  user_id: string;
  tasks: GamePassUserProgressTask[];
  xp_earned?: number;
  challenge_id?: string;
  "season_id#challenge_id"?: string;
  name?: string;
  description?: string;
  sort_order?: number;
  path?: string;
  path_availability?: ChallengePathAvailability;
  img_key?: string;
  rewards?: CreateChallengeRewardRequest[];
  reward_claimed?: boolean;
}

/** Challenge row derived from a user progress group entry. */
export type ProgressChallengeViewModel = {
  id: string;
  challenge_id: string;
  name: string;
  description?: string;
  sort_order: number;
  path_availability?: ChallengePathAvailability;
  rewards?: CreateChallengeRewardRequest[];
  progressEntry: GamePassUserChallengeProgress;
};

/** Task row derived from a user progress task entry. */
export type ProgressTaskViewModel = {
  id: string;
  name: string;
  description?: string;
  data_source?: string;
  xp_reward?: number;
  challenge_order: number;
  progressTask: GamePassUserProgressTask;
};

export interface GamePassUserProgressData {
  enrollment?: GamePassUserEnrollment;
  progress?: GamePassUserChallengeProgress[];
}

export interface GamePassUserProgressResponse {
  success: boolean;
  data?: GamePassUserProgressData;
}

/** Single enrollment row from
 *  `GET /game-pass-admin/v2/admin/users/{user_id}/enrollments`. The BE may
 *  embed a nested `season` object or flatten season metadata onto the row. */
export interface GamePassUserEnrollmentRecord {
  id?: string;
  season_id?: string;
  /** GP-6: `"SEASON"` | `"CAMPAIGN"`. Campaign rows also set `campaign_id`. */
  container_type?: string;
  /** Present when the enrollment is against a Monthly Challenge campaign. */
  campaign_id?: string | null;
  user_id?: string;
  username?: string;
  is_premium?: boolean;
  enrolled_at?: number | string;
  enrolledAt?: number | string;
  premium_purchased_at?: number | string | null;
  premiumPurchasedAt?: number | string | null;
  current_challenge_id?: string;
  current_challenge_index?: number;
  current_challenge?: string;
  name?: string;
  display_name?: string | null;
  season_name?: string;
  season_display_name?: string | null;
  status?: SeasonApiStatus;
  start_date?: string;
  end_date?: string;
  challenge_count?: number;
  task_count?: number;
  free_path_enabled?: boolean;
  premium_path_enabled?: boolean;
  season?: Partial<SeasonApiRecord>;
}

export interface GamePassUserEnrollmentsResponse {
  success: boolean;
  enrollments?: GamePassUserEnrollmentRecord[];
  data?: GamePassUserEnrollmentRecord[] | { enrollments?: GamePassUserEnrollmentRecord[] };
}

/** Season row rendered in the user-manager Game Pass accordion, enriched with
 *  the user's enrollment metadata from the enrollments listing. */
export type UserManagerAccordionSeason = SeasonApiRecord & {
  userEnrollment?: Partial<GamePassUserEnrollment>;
};

/** Response envelope for `GET /game-pass-admin/v2/admin/users/{user_id}/stats`. */
export interface GamePassUserStats {
  season_id?: string;
  p2p_wins?: number;
  current_following?: number;
  initial_following?: number;
  consecutive_days?: number;
  active_days?: number;
  daily_challenges_completed?: number | null;
  current_referrals?: number | null;
  initial_referrals?: number | null;
}

/** Response envelope for `GET /game-pass-admin/v2/admin/users/{user_id}/stats`. */
export interface GamePassUserStatsResponse {
  success?: boolean;
  data?: GamePassUserStats;
}

/** Request body for `POST /game-pass-admin/v2/admin/users/{user_id}/tasks/{task_id}/pass`. */
export interface GamePassPassUserTaskRequest {
  reason: string;
  bet_id?: string;
}

/** Inputs used to optimistically patch cached user progress after a manual pass. */
export type ManualTaskPassUpdateVariables = {
  taskId: string;
  reason: string;
  /** Season container id — mutually exclusive with `campaignId` for cache keys. */
  seasonId?: string;
  /** Campaign (Monthly Challenge) container id — mutually exclusive with `seasonId`. */
  campaignId?: string;
  challengeId: string;
  /** Optional sportsbook bet to associate with the manual pass. */
  betId?: string;
  /** Populates `user_id` on a synthesized progress entry when no cached row exists. */
  userId?: string;
  challenge?: {
    id?: string;
    challenge_id?: string;
    name?: string;
    sort_order?: number;
  };
};

/** Response envelope for `POST /game-pass-admin/v2/admin/users/{user_id}/tasks/{task_id}/pass`. */
export interface GamePassPassUserTaskResponse {
  success?: boolean;
  message?: string;
  task_id?: string;
  challenge_id?: string;
  season_id?: string;
  campaign_id?: string;
  auto_claimed?: boolean;
}

/** Request body for
 *  `POST /gamepass/v2/admin/users/{user_id}/tasks/{task_id}/increment`. */
export interface GamePassIncrementUserTaskRequest {
  amount?: number;
  bet_id?: string;
  reason?: string;
}

/** Inputs for the increment-task mutation (support "+N" remediation). */
export type IncrementUserTaskVariables = {
  userId: string;
  taskId: string;
  reason: string;
  amount?: number;
  betId?: string;
  /** Used to invalidate the right progress cache entry after a successful bump. */
  seasonId?: string;
  campaignId?: string;
};

/** Response body for
 *  `POST /gamepass/v2/admin/users/{user_id}/tasks/{task_id}/increment`. */
export interface GamePassIncrementUserTaskResponse {
  success?: boolean;
  message?: string;
  task_id?: string;
  challenge_id?: string;
  season_id?: string;
  campaign_id?: string;
  count?: number;
  streak_count?: number;
  streak_best?: number;
  target_value?: number;
  completed?: boolean;
  auto_claimed?: boolean;
}

// ─── Challenges API ───────────────────────────────────────────────────────
//
// `POST /game-pass-admin/v2/admin/challenges` creates a single challenge under a
// season. The Create Season wizard persists the challenge the first time the
// admin clicks Save on a challenge card. Subsequent metadata edits use the
// partial PUT endpoint; rewards still have their own dedicated endpoint.

/** Allowed values for the BE `data_source` enum on challenges. */
export type ChallengeDataSource = "SPORTSBOOK" | "SOCIAL" | "MIXED";

/** Allowed values for the BE `path_availability` enum on challenges. */
export type ChallengePathAvailability = "FREE" | "PREMIUM" | "BOTH";

/** Reward tier accepted by the challenge create endpoint. Derived from the
 *  challenge's `path_availability` (the Free / Premium tab the challenge was
 *  authored under): `FREE` → `free`, `PREMIUM` → `premium`, `BOTH` → both. */
export type ChallengeRewardTier = "free" | "premium";

/** Reward currency accepted by the challenge create/update endpoints. The BE
 *  denominates Game Pass rewards in one of two currencies — the play-money
 *  balance (`play`) or the redeemable value balance (`value`). The UI's
 *  `RewardType` pills map onto these via `REWARD_TYPE_TO_CHALLENGE_CURRENCY`
 *  (see `src/queries/game-pass/challengeRewards.ts`). */
export type ChallengeRewardCurrency = "play" | "value";

/** Reward payload accepted by the challenge create/update endpoints.
 *
 *  Built from the challenge editor's reward rows: `amount` from the Amount
 *  input, `currency` from the reward-type → currency mapping, and `tier` from
 *  the challenge's `path_availability`. See `mapChallengeRewardsToRequest`. */
export interface CreateChallengeRewardRequest {
  amount: number;
  /** `play` / `value` for currency rewards; the bonus category (e.g.
   *  `free_pick`) for bonus rewards. */
  currency: ChallengeRewardCurrency | string;
  tier: ChallengeRewardTier;
  /** Bonus rewards only — the BE grant identifier (e.g. `free_cash_pick`).
   *  Absent for the play/value currency rewards. */
  reward_type?: string;
  /** Bonus rewards only — the bonus's tunable params, coerced to their declared
   *  numeric / string types (see `mapChallengeRewardsToRequest`). */
  params?: Record<string, string | number>;
}

/** Request body for `POST /game-pass-admin/v2/admin/challenges`.
 *  Rewards are not sent on create — persist them via
 *  `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}/reward`. */
export interface CreateChallengeRequest {
  season_id: string;
  name: string;
  sort_order: number;
  description?: string;
  icon_emoji?: string;
  /** S3 object key of the uploaded challenge icon (see
   *  `uploadGamePassImage`). Omitted when the admin didn't attach an icon. */
  img_key?: string;
  data_source?: ChallengeDataSource;
  path_availability?: ChallengePathAvailability;
}

/** Challenge record returned by `POST /game-pass-admin/v2/admin/challenges`. */
export interface ChallengeApiRecord {
  id?: string;
  challenge_id?: string;
  season_id: string;
  name: string;
  sort_order: number;
  description?: string | null;
  icon_emoji?: string | null;
  img_key?: string | null;
  /** Fully-qualified, ready-to-render URL for the challenge icon, returned by
   *  the BE on challenge create / update / list responses. Preferred over
   *  rebuilding a URL from `img_key` (which the FE could only do by guessing
   *  the CDN base) — render this directly. */
  image_url?: string | null;
  data_source?: ChallengeDataSource | null;
  path_availability?: ChallengePathAvailability | null;
  rewards?: CreateChallengeRewardRequest[] | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateChallengeResponse {
  success: boolean;
  challenge?: ChallengeApiRecord;
  data?: ChallengeApiRecord;
}

/** Top-level success envelope for `GET /game-pass-admin/v2/admin/challenges`. */
export interface ChallengesListResponse {
  success: boolean;
  challenges: ChallengeApiRecord[];
}

/** Partial body for `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}`. */
export interface UpdateChallengeRequest {
  name?: string;
  sort_order?: number;
  description?: string;
  /** S3 object key of a newly-uploaded challenge icon. Only sent when the
   *  admin attached / changed the icon — omitted otherwise so an edit that
   *  doesn't touch the image leaves the stored `img_key` untouched. An
   *  explicit `null` clears a previously-saved icon server-side. */
  img_key?: string | null;
}

export interface UpdateChallengeResponse {
  success: boolean;
  challenge?: ChallengeApiRecord;
  data?: ChallengeApiRecord;
}

/** Request body for `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}/reward`. */
export interface UpdateChallengeRewardRequest {
  /** Authoritative replacement for the challenge's reward set. Built from the
   *  editor rows via `mapChallengeRewardsToRequest`. Send `[]` to clear all. */
  rewards: CreateChallengeRewardRequest[];
}

export interface UpdateChallengeRewardResponse {
  success: boolean;
  challenge?: ChallengeApiRecord;
  data?: ChallengeApiRecord;
}

/** Top-level envelope for `DELETE /game-pass-admin/v2/admin/challenges/{id}`.
 *  The BE returns just a success flag (no body payload). */
export interface DeleteChallengeResponse {
  success: boolean;
}

// ─── Tasks API ────────────────────────────────────────────────────────────
//
// `POST /game-pass-admin/v2/admin/tasks` creates a single task on a season's
// challenge. The frontend currently posts via this endpoint directly from
// the Create Task modal (one network call per Create click). Season must be
// in `draft` or `scheduled` status — gated by the backend; on a 4xx we
// surface the API message to the admin and leave the modal open.
//
// Several fields in the request body have no UI counterpart yet
// (`completion_mode`, `counting_method`, `path`, `xp_reward`, `rules`,
// `min_odds_american`, etc.). The mapper in `useCreateTask.ts` sends safe
// defaults / nulls for those — see the field-by-field comments there. As
// the Create Task screen grows additional inputs, swap the defaults for
// the corresponding draft values one at a time without changing this
// type shape.

/** Allowed values for the BE `path` enum. Today the UI only ever produces
 *  `"FREE"` because tier selection is per-season (not per-task), but the
 *  union is kept so a future per-task tier picker can populate it without
 *  re-typing the field. */
export type TaskPath = "FREE" | "PREMIUM";

// ───────────────────────────────────────────────────────────────────────────
// BE task contract — string constants (single source of truth)
//
// Every BE wire string the Game Pass task mappers / round-trip read or write
// lives here exactly once. The union types below are *derived* from these
// objects so a value and its type can never drift apart, and consumers
// (`useCreateTask`, `useUpdateTask`, `SeasonDetails`) reference the constant
// instead of re-typing the literal. To add / rename a wire value, edit it here
// only.
// ───────────────────────────────────────────────────────────────────────────

/** BE `data_source` enum (`SPORTSBOOK`/`SOCIAL`/`CASINO`). NOTE the wire value
 *  for socials is the singular `SOCIAL` — the Create Task modal's Task Type
 *  pill uses `SOCIALS` (see `TaskType`), so the create/update mappers translate
 *  via `TASK_TYPE_TO_DATA_SOURCE` rather than shipping the pill value verbatim.
 *  Sending `SOCIALS` is rejected by the BE with a 422. */
export const TASK_DATA_SOURCE = {
  SPORTSBOOK: "SPORTSBOOK",
  SOCIAL: "SOCIAL",
  CASINO: "CASINO",
} as const;
export type TaskDataSource =
  (typeof TASK_DATA_SOURCE)[keyof typeof TASK_DATA_SOURCE];

/** BE `event_type` enum — a **SOCIAL-only** field (per the V2 Game Pass
 *  API guide §7). It names the social event stream the task engine
 *  evaluates a SOCIAL task against; the BE rejects it on Sportsbook /
 *  Casino tasks (`event_type is only valid on a SOCIAL task`). The field
 *  itself is gated by the `task_event_type` flag — when off it's silently
 *  dropped at create and the SOCIAL task must be scoped by `rules[]` only.
 *  A SOCIAL task must end up scoped by `event_type` OR at least one rule.
 *  The PAM currently scopes SOCIAL tasks via `rules[]` (see
 *  `buildSocialRules`) and sends `event_type: null` on every task. */
export const TASK_EVENT_TYPE = {
  DAILY_LOGIN: "daily_login",
  P2P_PICK_WON: "p2p_pick_won",
  USER_FOLLOWED: "user_followed",
  USER_REFERRED: "user_referred",
} as const;
export type TaskEventType =
  (typeof TASK_EVENT_TYPE)[keyof typeof TASK_EVENT_TYPE];

/** BE-classified challenge type (FE-2579). Shipped per task on the user
 *  progress response (`tasks[].challenge_type`) so the PAM can pick the right
 *  history table without re-deriving it from `data_source` + marker rules. The
 *  BE resolves it as:
 *    - REBET      → others re-bet the user's picks (inbound) or the user places
 *                   re-bets (actor); `rebets_received` rule marker present
 *    - SPORTSBOOK → sports bets, `data_source=SPORTSBOOK`, no re-bet marker
 *    - CASINO     → casino play, `data_source=CASINO`
 *    - FOLLOWER   → gain followers
 *    - REFERRAL   → refer users
 *    - POKER      → poker hands
 *    - LOGIN      → daily login streak
 *    - SOCIAL     → other / unclassified social
 *    - UNKNOWN    → safe fallback, nothing matched
 *  Kept as the primary discriminator; the `data_source` + rule heuristic stays
 *  as a fallback for payloads shipped before this field lands. */
export const CHALLENGE_TYPE = {
  REBET: "REBET",
  SPORTSBOOK: "SPORTSBOOK",
  CASINO: "CASINO",
  FOLLOWER: "FOLLOWER",
  REFERRAL: "REFERRAL",
  POKER: "POKER",
  LOGIN: "LOGIN",
  SOCIAL: "SOCIAL",
  UNKNOWN: "UNKNOWN",
} as const;
/** Left open to `string` so a future challenge type the BE adds doesn't have to
 *  fight the compiler. */
export type ChallengeType =
  | (typeof CHALLENGE_TYPE)[keyof typeof CHALLENGE_TYPE]
  | (string & {});

/** BE `bet_type` enum. The `"ANY"` UI option maps to `null` on the wire (the
 *  BE treats absent as "any"), so this set intentionally excludes it — `ANY`
 *  is the "no restriction" sentinel that ships as `null`. */
export const TASK_BET_TYPE = {
  SINGLE: "SINGLE",
  PARLAY: "PARLAY",
  SGP: "SGP",
  LIVE: "LIVE",
} as const;
export type TaskBetType = (typeof TASK_BET_TYPE)[keyof typeof TASK_BET_TYPE];

/** BE `outcome_required` enum. Mirrors the Qualifier pill row in the Create
 *  Task modal. */
export const TASK_OUTCOME_REQUIRED = {
  WIN: "WIN",
  WIN_OR_PUSH: "WIN_OR_PUSH",
  ANY_SETTLED: "ANY_SETTLED",
} as const;
export type TaskOutcomeRequired =
  (typeof TASK_OUTCOME_REQUIRED)[keyof typeof TASK_OUTCOME_REQUIRED];

/** Casino Action enum (Play / Win). Mirrors the Action pill shown on the
 *  Casino trigger of the Create Task modal; shipped as the value of the
 *  `outcome` rule. The Play pill maps to the wire value `"settled"` (FE-2005)
 *  — not the literal `"PLAY"` string the UI uses internally. Only meaningful
 *  on casino tasks (`data_source = CASINO`). */
export const TASK_CASINO_ACTION = {
  PLAY: "settled",
  WIN: "WIN",
} as const;
export type TaskCasinoAction =
  (typeof TASK_CASINO_ACTION)[keyof typeof TASK_CASINO_ACTION];

/** Wire values of the sportsbook `outcome` rule on a rebets_received task
 *  (GP-13/GP-14 wiring guide). Lowercase — distinct from the uppercase
 *  `TASK_OUTCOME_REQUIRED` column enum and from the casino `outcome` rule's
 *  mixed-case values. On a hand-authored `rules[]` task the BE only enforces
 *  the RULE; the top-level `outcome_required` column is ignored, so a "must
 *  win" rebets task must carry `outcome: "win"` as a rule. */
export const TASK_REBETS_OUTCOME = {
  WIN: "win",
  SETTLED: "settled",
  PENDING: "pending",
} as const;
export type TaskRebetsOutcome =
  (typeof TASK_REBETS_OUTCOME)[keyof typeof TASK_REBETS_OUTCOME];

/** Field a task condition rule constrains (Game Pass V2). The API encodes the
 *  trigger selections as rules rather than top-level columns:
 *    - `sport_id`                   ← the Sport Specific tree (sportsbook tasks)
 *    - `provider`                   ← the casino provider picker (casino tasks)
 *    - `game_id`                    ← the casino game picker (casino tasks)
 *    - `outcome`                    ← the casino Action pill, Play / Win (casino tasks)
 *    - `money_type`                 ← the casino Bet Currency picker (casino tasks)
 *                                     and the sportsbook Bet Currency multi-select
 *    - `bet_amount`                 ← legacy Minimum Bet Amount threshold; read-only
 *                                     fallback for tasks persisted before the
 *                                     value moved to the top-level
 *                                     `min_bet_amount` column (sportsbook tasks)
 *    - `is_parlay`                  ← the Parlay bet-type pill (sportsbook tasks)
 *    - `num_legs`                   ← the Parlay Number of Legs input (sportsbook tasks)
 *    - `odds_american`              ← the Minimum Odds (`gte`) and Maximum Odds
 *                                     (`lte`) inputs (sportsbook tasks)
 *    - `is_new_relationship`        ← the Followers social task type
 *    - `consecutive_days`           ← the Consecutive Login Days + Consecutive
 *                                     Days Playing a Pick social task types
 *    - `opponent_account_age_days`  ← the P2P Wins social task type (anti-farming)
 *    - `shared_device_flag`         ← discriminates Consecutive Days Playing a
 *                                     Pick from the login streak (both share
 *                                     `consecutive_days`) */
export const TASK_RULE_FIELD = {
  SPORT: "sport_id",
  /** Sport league / tournament selected under a sport in the Sport Specific tree —
   *  ships the tournament id (`league_id` rule). Legacy tasks may still carry a
   *  `league` rule with a human-readable name; see the read path in
   *  `mapApiTaskToDraft`. */
  LEAGUE_ID: "league_id",
  /** @deprecated Legacy league rule keyed by display name — prefer `LEAGUE_ID`. */
  LEAGUE: "league",
  PROVIDER: "provider",
  GAME: "game_id",
  /** Casino Action (Play / Win) — the BE keys this off the generic `outcome`
   *  rule field, NOT `casino_action` (which the BE rejects with a 422). */
  OUTCOME: "outcome",
  /** Casino Bet Currency (rebet_cash / rebet_coin) → the BE `money_type` rule. */
  MONEY_TYPE: "money_type",
  BET_AMOUNT: "bet_amount",
  IS_PARLAY: "is_parlay",
  /** RETIRED Monthly "Rebets" marker (FE-2441): counted re-bets the user
   *  PLACED on other people's picks. Superseded by
   *  {@link TASK_RULE_FIELD.REBETS_RECEIVED} in FE-2535 — never authored
   *  anymore, but still recognized on read so legacy saved tasks open as
   *  Rebets (see `monthlyDetailsMapper`) and upgrade on their next save. */
  IS_REBET: "is_rebet",
  /** Monthly "Rebets" challenge marker (FE-2535): the task credits the pick
   *  OWNER when someone else re-bets their pick. The only marker the create
   *  mapper ships; also the read-side discriminator between a Rebets task and
   *  a Sportsbook Picks task, which share every other trigger column. */
  REBETS_RECEIVED: "rebets_received",
  /** GP-14 modifier for a `rebets_received` task ONLY (the API 422s it on any
   *  other task): progress is credited the instant a re-bet is placed
   *  (pending) rather than at settlement, and a voided pending re-bet reverses
   *  the credit while the task is in progress. Authored from the Rebets
   *  editor's "Play" action pill; "Win" instead ships an `outcome: "win"` rule
   *  (see `TASK_REBETS_OUTCOME`) — the wiring guide forbids pairing
   *  `count_on_placement` with a settlement outcome rule, and the
   *  `outcome_required` column is not enforced on rules[] tasks (FE-2535). */
  COUNT_ON_PLACEMENT: "count_on_placement",
  NUM_LEGS: "num_legs",
  ODDS_AMERICAN: "odds_american",
  IS_NEW_RELATIONSHIP: "is_new_relationship",
  CONSECUTIVE_DAYS: "consecutive_days",
  OPPONENT_ACCOUNT_AGE_DAYS: "opponent_account_age_days",
  SHARED_DEVICE_FLAG: "shared_device_flag",
  /** Refer-a-user social mechanic (`user_referred` event) — the referred user
   *  making a qualifying deposit is the milestone the task tracks. */
  REFERRED_USER_MADE_QUALIFYING_DEPOSIT: "referred_user_made_qualifying_deposit",
} as const;
/** Left open to `string` so a future rule field the BE adds doesn't have to
 *  fight the compiler. */
export type TaskRuleField =
  | (typeof TASK_RULE_FIELD)[keyof typeof TASK_RULE_FIELD]
  | (string & {});

/** Comparison operator on a task condition rule. A single selected id uses
 *  `eq` with a scalar `value`; multiple ids use `in` with an array `value`;
 *  numeric thresholds use `gte` (lower bound, e.g. Minimum Odds) or `lte`
 *  (upper bound, e.g. Maximum Odds) with a scalar number `value`. The
 *  negation operators `neq` (scalar inequality) and `not_in` (array
 *  exclusion) complete the set defined by the Game Pass V2 API guide §8;
 *  `gt` / `lt` are intentionally absent (use `gte` / `lte`). */
export const TASK_RULE_OP = {
  EQ: "eq",
  NEQ: "neq",
  IN: "in",
  NOT_IN: "not_in",
  GTE: "gte",
  LTE: "lte",
} as const;
/** Left open to `string` so a future operator the BE adds still type-checks. */
export type TaskRuleOp =
  | (typeof TASK_RULE_OP)[keyof typeof TASK_RULE_OP]
  | (string & {});

/** Single condition rule attached to a task. The BE expects a flexible
 *  list-of-ops; the modal authors `sport_id` / `provider` / `game_id` rules
 *  from the Sport Specific tree and the casino pickers (see `buildTaskRules`).
 *  `value` is a scalar for `eq` and a string array for `in`. */
export interface TaskRule {
  op: TaskRuleOp;
  field: TaskRuleField;
  value: string | number | boolean | null | ReadonlyArray<string>;
}

/** Request body for `POST /game-pass-admin/v2/admin/tasks`. Field names mirror
 *  the BE contract verbatim — any rename here is a coordinated BE+FE
 *  change. Nullables match what the BE explicitly accepts (per the spec
 *  example doc); never widen a field to `undefined` unless the BE
 *  actually omits it from the response. */
export interface CreateTaskRequest {
  season_id: string;
  /** The parent challenge id. The Create Season wizard sends this once the
   *  challenge has been persisted; otherwise it falls back to `null` so the
   *  pre-save local-only flow keeps working. */
  challenge_id: string | null;
  /** Display name of the parent challenge. */
  challenge: string;
  /** Zero-based position of this task within its parent challenge. The create
   *  flow stamps the next slot (existing task count) so tasks default to
   *  creation order; the drag-reorder flow later overwrites it. This is the
   *  single ordering key — the task analogue of a challenge's `sort_order`. */
  challenge_order: number;
  name: string;
  description: string;
  /** S3 object key of the uploaded task image (see `uploadGamePassImage`).
   *  Omitted when the admin didn't attach an image. */
  img_key?: string;
  path: TaskPath;
  completion_mode: string;
  data_source: TaskDataSource;
  /** SOCIAL-only social event stream the task engine evaluates against
   *  (one of `daily_login` / `p2p_pick_won` / `user_followed` /
   *  `user_referred`, gated by the `task_event_type` flag). `null` for
   *  Sportsbook / Casino tasks (the BE rejects it there) and for SOCIAL
   *  tasks the PAM scopes via `rules[]` instead. */
  event_type: TaskEventType | null;
  counting_method: string;
  completion_condition: string;
  /** Numeric goal for sportsbook/social tasks (e.g. "place 3 bets"). For
   *  sportsbook COUNT tasks this is driven by the "Number of Bets" input; for
   *  casino tasks it carries the Action Amount (number of qualifying plays /
   *  wins). `null` when blank. */
  target_value: number | null;
  outcome_required: TaskOutcomeRequired | null;
  bet_type: TaskBetType | null;
  min_odds_american: number | null;
  // NOTE: the Maximum Odds requirement is NOT a top-level column — it ships
  // exclusively as an `odds_american` `lte` condition rule (see
  // `buildTaskRules`). Minimum Odds is the mirror `gte` rule. `min_odds_american`
  // is retained as a top-level column for backwards compatibility with the
  // pre-rules read path.
  min_leg_count: number | null;
  /** Minimum qualifying bet amount for a sportsbook task — the smallest stake
   *  that counts toward the task. Top-level column (NOT a rule). `null` for
   *  casino / social tasks, or when the sportsbook admin left it blank. */
  min_bet_amount: number | null;
  exclude_rebet_tokens: boolean;
  xp_reward: number;
  /** Condition rules (Game Pass V2). Carries every trigger selection: the
   *  Sport Specific selection as a `sport_id` rule; the casino provider / game
   *  selections as `provider` / `game_id` rules; and the casino Action as an
   *  `outcome` rule with the Bet Currency as a `money_type` rule. These
   *  replace the old top-level `sports` / `casino_providers` / `casino_games`
   *  columns. (The Minimum Bet Amount is a top-level `min_bet_amount` column,
   *  not a rule.) Empty when nothing is selected. See `buildTaskRules`. */
  rules: ReadonlyArray<TaskRule>;
  is_active: boolean;
  /** Per-task submission mode override. Casino tasks are always `"AUTO"`;
   *  sportsbook tasks may be `"AUTO"` or `"MANUAL"`. Omitted for social tasks. */
  task_submission_mode?: SeasonTaskSubmissionMode;
}

/** Full task record returned by the create endpoint. Lifecycle
 *  timestamps + ids are populated by the backend. */
export interface TaskApiRecord extends CreateTaskRequest {
  id: string;
  /** Fully-qualified, ready-to-render URL for the task image, returned by the
   *  BE on task create / update responses. Preferred over rebuilding a URL
   *  from `img_key` — render this directly. */
  image_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Top-level envelope for `POST /game-pass-admin/v2/admin/tasks`. Matches the
 *  `{ success, task }` shape used by `POST /game-pass-admin/v2/admin/seasons`. */
export interface CreateTaskResponse {
  success: boolean;
  task?: TaskApiRecord;
  data?: TaskApiRecord;
}

/** Request body for `PUT /game-pass-admin/v2/admin/tasks/{task_id}`.
 *
 * Partial-update semantics: only send fields you want to change. The BE
 * leaves untouched anything that isn't present in the body. Optional
 * fields can be cleared by sending `null` explicitly — the "clearable"
 * set per the spec is `bet_type`, `min_odds_american`, `min_leg_count`,
 * `outcome_required`, `sports`, `rules`. Every other field is
 * non-nullable: when present it must carry a real value.
 *
 * Identity fields (`season_id`, `challenge_id`, `challenge`) are
 * intentionally absent — they're immutable for the lifetime of the task
 * and the BE rejects attempts to move a task between challenges/seasons
 * via PUT. Use a separate create+delete flow if that ever becomes a
 * product requirement.
 *
 * `challenge_order` IS mutable: it's the task's zero-based position within
 * its parent challenge and the drag-reorder flow (`ChallengeCard` →
 * `handleTaskDragEnd`) PUTs a new value to persist a manual reorder.
 */
export interface UpdateTaskRequest {
  name?: string;
  description?: string;
  /** S3 object key of a newly-uploaded task image. Only sent when the admin
   *  attached / changed the image — omitted otherwise so an edit that doesn't
   *  touch the image leaves the stored `img_key` untouched. An explicit `null`
   *  clears a previously-saved image server-side. */
  img_key?: string | null;
  path?: TaskPath;
  completion_mode?: string;
  data_source?: TaskDataSource;
  counting_method?: string;
  completion_condition?: string;
  /** Numeric goal for sportsbook/social tasks; the Action Amount for casino
   *  tasks. Sportsbook COUNT tasks source this from the "Number of Bets" input.
   *  `null` when blank. */
  target_value?: number | null;
  /** Clearable: send `null` to remove the qualifier requirement. */
  outcome_required?: TaskOutcomeRequired | null;
  /** Clearable: send `null` to remove the bet-type restriction. */
  bet_type?: TaskBetType | null;
  /** Clearable: send `null` to remove the minimum-odds requirement. */
  min_odds_american?: number | null;
  // NOTE: the Maximum Odds requirement is NOT a top-level column — it ships
  // exclusively as an `odds_american` `lte` condition rule (see
  // `buildTaskRules`). Sending `rules` (always rebuilt wholesale on update)
  // with no `odds_american` `lte` entry clears the maximum-odds requirement.
  /** Clearable: send `null` to remove the minimum-legs requirement. */
  min_leg_count?: number | null;
  /** Minimum qualifying bet amount (sportsbook). Top-level column (NOT a rule).
   *  Clearable: send `null` to remove the requirement. `null` for casino /
   *  social tasks. */
  min_bet_amount?: number | null;
  /** Zero-based position of the task within its parent challenge. Written only
   *  by the drag-reorder flow (`ChallengeCard` → `handleTaskDragEnd`); a normal
   *  task edit omits it so the stored order is left untouched. This is the same
   *  key the create flow stamps and the list sorts by — there is no separate
   *  `stage_index`. */
  challenge_order?: number | null;
  exclude_rebet_tokens?: boolean;
  xp_reward?: number;
  /** Every trigger selection, encoded as `sport_id` / `provider` / `game_id`
   *  / `outcome` / `money_type` / `bet_amount` rules (Game Pass V2). Send `[]`
   *  to clear every rule on the task; `null` is also accepted by the BE as
   *  "remove all rules". See `buildTaskRules`. */
  rules?: ReadonlyArray<TaskRule> | null;
  is_active?: boolean;
  /** Per-task submission mode override (`"AUTO"` | `"MANUAL"`). `null` or
   *  absent for social tasks and legacy records. */
  task_submission_mode?: SeasonTaskSubmissionMode | null;
}

/** Top-level envelope for `PUT /game-pass-admin/v2/admin/tasks/{task_id}`.
 *  Mirrors the create envelope so consumers can pull `.task` regardless
 *  of which mutation they fired. */
export interface UpdateTaskResponse {
  success: boolean;
  task?: TaskApiRecord;
  data?: TaskApiRecord;
}

/** Single record returned by `GET /game-pass-admin/v2/admin/tasks?season_id=…`.
 *
 *  The listing payload is wider than `CreateTaskRequest` (it carries the
 *  BE-assigned id, audit timestamps, and a handful of fields the create
 *  flow never sends — `counting_method`, `completion_condition`,
 *  `target_value`, the nested `tasks` sub-list, etc.), and
 *  several columns BE returns as `null` for tasks created before they
 *  existed. We model it loosely (most fields optional / nullable) so the
 *  Season Details mapper can read the few fields it renders without the
 *  type fighting a real-world partial payload.
 *
 *  `challenge_id` is nullable on purpose: tasks created via the legacy
 *  pre-persist flow land with `challenge_id: null` and only a `challenge`
 *  display-name to associate them with a card — the grouping logic in
 *  `SeasonDetails` falls back to the display name when the id is absent. */
export interface TaskListRecord {
  id: string;
  season_id: string;
  name: string;
  description?: string | null;
  /** Display name of the parent challenge (e.g. `"Challenge 1"`). Used as
   *  the grouping fallback when `challenge_id` is null. */
  challenge?: string | null;
  /** BE-assigned parent challenge id. `null` for tasks created before the
   *  challenge was persisted — group by `challenge` (display name) in that
   *  case. */
  challenge_id?: string | null;
  /** Zero-based position of this task within its parent challenge. Stamped by
   *  the backend on create (incrementing per task) and overwritten by the
   *  drag-reorder flow. This is the single key the task list sorts by — the
   *  task analogue of a challenge's `sort_order` (see
   *  `sortTasksByChallengeOrder`). `null` only for legacy tasks created before
   *  the field existed — those sort last. */
  challenge_order?: number | null;
  path?: TaskPath | string | null;
  completion_mode?: string | null;
  img_key?: string | null;
  /** Fully-qualified, ready-to-render URL for the task image, returned by the
   *  BE on the task listing. Preferred over rebuilding a URL from `img_key` —
   *  render this directly. */
  image_url?: string | null;
  is_active?: boolean;
  xp_reward?: number | null;
  data_source?: TaskDataSource | string | null;
  bet_type?: TaskBetType | string | null;
  min_odds_american?: number | null;
  max_odds_american?: number | null;
  min_leg_count?: number | null;
  /** Minimum qualifying bet amount (sportsbook) — top-level column. `null` for
   *  casino / social tasks or tasks authored without a minimum. */
  min_bet_amount?: number | null;
  outcome_required?: TaskOutcomeRequired | string | null;
  exclude_rebet_tokens?: boolean | null;
  sports?: ReadonlyArray<string> | null;
  /** ─── Casino trigger fields (null for non-casino tasks) ───────────── */
  casino_providers?: ReadonlyArray<string> | null;
  casino_games?: ReadonlyArray<string> | null;
  /** BE-resolved catalogue entries for the task's `game_id` rule values — one
   *  per game (gamepass-api #371). `[]` / absent for non-casino, provider-only,
   *  or unresolved tasks. The flat `game_name` / `provider_name` /
   *  `game_image_url` scalar mirrors of `games[0]` are intentionally not
   *  modelled — the BE keeps them for backward compatibility only. */
  games?: ReadonlyArray<TaskResolvedGame> | null;
  rules?: ReadonlyArray<TaskRule> | null;
  counting_method?: string | null;
  completion_condition?: string | null;
  /** SOCIAL event stream (`daily_login` / `user_followed` / …). Monthly create
   *  mappers send this for Login Streak (`daily_login`); the BE may echo it
   *  as `null`, in which case the `consecutive_days` rule is the discriminator. */
  event_type?: TaskEventType | string | null;
  /** BE-classified challenge type (FE-2579 / FE-2647). */
  challenge_type?: ChallengeType;
  /** Numeric goal for the task (e.g. "place 3 bets"). Maps to the
   *  "Number of bets" field in the Create/Edit Task modal. */
  target_value?: number | null;
  /** Legacy top-level Bet Currency column (money types) carried by tasks
   *  persisted before the V2 `money_type` rule. Used as the fallback in
   *  `resolveBetCurrenciesFromRules` when no `money_type` rule is present. */
  required_currencies?: ReadonlyArray<string> | null;
  /** Per-task submission mode override. Absent on social tasks and legacy
   *  records — defaults to the parent season's mode at runtime. */
  task_submission_mode?: SeasonTaskSubmissionMode | null;
  created_at?: string;
  updated_at?: string;
}

/** A game the BE resolved against the catalogue for a casino task's `game_id`
 *  rule, shipped on admin task reads (gamepass-api #370/#371) so the picker can
 *  label selection chips without joining `/casino-games`. Best-effort: every
 *  field defaults to `""` on a catalogue miss — treat empty as "unknown" and
 *  fall back to the catalogue lookup. `game_image_url` is the *game's* art,
 *  distinct from the task-level `image_url` (the task's authored image). */
export interface TaskResolvedGame {
  game_id: string;
  /** Aggregator/provider slug (machine key, e.g. `"pragmatic"`). */
  provider: string;
  /** Human studio label (e.g. `"Pragmatic Play"`). */
  provider_name: string;
  game_name: string;
  game_image_url: string;
}

/** Top-level envelope for `DELETE /game-pass-admin/v2/admin/tasks/{task_id}`.
 *  Mirrors `DeleteChallengeResponse` — the BE returns just a success flag
 *  (no body payload). */
export interface DeleteTaskResponse {
  success: boolean;
}

/** Top-level success envelope for `GET /game-pass-admin/v2/admin/tasks`. Matches
 *  the seasons/challenges listing shape: the array is co-located with the
 *  `success` flag at the top level rather than wrapped in `{ data }`. Generic
 *  over the record type so campaign-scoped callers can narrow `tasks` to
 *  {@link CampaignTaskListRecord} without a downcast (see
 *  {@link CampaignTasksListResponse}); defaults to the season shape. */
export interface TasksListResponse<T extends TaskListRecord = TaskListRecord> {
  success: boolean;
  tasks: T[];
}

// ─── Seasons listing UI model ────────────────────────────────────────────
//
// The shapes below are what `SeasonCard` / `SeasonsList` ultimately
// render. They live alongside the API contract above so the data /
// query layer (`src/queries/game-pass/seasonsMappers.ts`) can depend
// on `src/types/` only — keeping the dependency direction one-way
// (component → types, query → types) and matching the convention used
// by `src/types/affiliates.ts` / `src/types/staff.ts`. The data
// constants and mock fixtures stay in
// `src/components/GamePass/SeasonsList/seasonsListData.ts`, which now
// re-exports these types so existing consumers keep compiling
// untouched.

/** Canonical UI filter-tab id enum used by the Seasons listing
 *  page's tablist. Each value is also the URL search-param the page
 *  persists the active filter under (`?filter=<id>`), so changing one
 *  of these tokens is a breaking change for any deep-linked URL.
 *
 *  Same `const … as const` pattern as `SEASON_API_STATUS` above so
 *  the mappers can switch on `SEASONS_FILTER_ID.SCHEDULED` instead of
 *  the raw `"scheduled"` literal. */
export const SEASONS_FILTER_ID = {
  TIMELINE: "timeline",
  ALL: "all",
  SCHEDULED: "scheduled",
  DRAFTS: "drafts",
  COMPLETED: "completed",
  CANCELED: "canceled",
} as const;

/** String-literal union derived from the canonical
 *  `SEASONS_FILTER_ID` object. */
export type SeasonsFilterId =
  (typeof SEASONS_FILTER_ID)[keyof typeof SEASONS_FILTER_ID];

/** Filter tabs for the Monthly landing page. Mirrors `SEASONS_FILTER_ID`
 *  one-for-one, including the standalone "Scheduled" tab (upcoming campaigns
 *  also surface under "Next Month" on the Timeline, but admins expect a
 *  dedicated tab like Seasons has). Canonical here (alongside
 *  `SEASONS_FILTER_ID`) so the query layer's `campaignsMappers` can map a tab
 *  onto its `?status=` set without importing from the component layer. Tokens
 *  are URL-safe for the `?filter=` search param. Re-exported from
 *  `monthlyConfigurationData` for the page. */
export const MONTHLY_FILTER_ID = {
  TIMELINE: "timeline",
  ALL: "all",
  SCHEDULED: "scheduled",
  DRAFTS: "drafts",
  COMPLETED: "completed",
  CANCELED: "canceled",
} as const;

export type MonthlyFilterId =
  (typeof MONTHLY_FILTER_ID)[keyof typeof MONTHLY_FILTER_ID];

/** Lifecycle bucket a season is partitioned into on the listing page.
 *  Distinct from `SeasonApiStatus` because the BE enum has more values
 *  than the UI has columns — `grace` collapses into `active`, `locked`
 *  collapses into `scheduled`. See `seasonStatusToBucket` for the
 *  mapping. */
export const SEASON_LIFECYCLE_BUCKET = {
  ACTIVE: "active",
  SCHEDULED: "scheduled",
  DRAFTS: "drafts",
  COMPLETED: "completed",
  CANCELED: "canceled",
} as const;

export type SeasonLifecycleBucket =
  (typeof SEASON_LIFECYCLE_BUCKET)[keyof typeof SEASON_LIFECYCLE_BUCKET];

/** Single entry in the tablist strip. The `label` is rendered text
 *  (sourced from `AppStrings`), the `id` is the URL token. */
export interface SeasonsFilterTab {
  id: SeasonsFilterId;
  label: string;
}

/** Visual tone for the top-right status badge on a season card. New
 *  tones must be added here AND mirrored in `SeasonCard.module.css`. */
export type SeasonStatusTone =
  | "active"
  | "upNext"
  | "draft"
  | "completed"
  | "canceled";

export interface SeasonStatus {
  label: string;
  tone: SeasonStatusTone;
}

export interface SeasonCountdown {
  /** Translated prefix, e.g. "Ends in" / "Starts in" / "Locks in". */
  prefix: string;
  /** The emphasised numeric portion ("24 days"). Kept as a free-form
   *  string so callers can plug in `pluralize` or i18n helpers later. */
  value: string;
}

export interface SeasonMetric {
  id: string;
  /** Singular or plural label — pluralisation is the data layer's job. */
  label: string;
  value: number | string;
}

/** Bottom-row affordance on the card.
 *  - `"locked"` renders a muted pill alongside the metrics.
 *  - `"edit"` renders a pencil button alongside the metrics.
 *  - `"seeReports"` renders a See Reports link on its own row below
 *    the metrics — used by completed / canceled seasons.
 *  - `"none"` hides every affordance. */
export type SeasonTrailingKind = "locked" | "edit" | "seeReports" | "none";

export interface SeasonData {
  id: string;
  name: string;
  /** Pre-formatted date range, e.g. "May 1 - May 31, 2026". Keeping the
   *  formatting outside the card avoids dragging a date library into a
   *  purely-presentational component. */
  dateRange: string;
  countdown?: SeasonCountdown;
  metrics: ReadonlyArray<SeasonMetric>;
  status?: SeasonStatus;
  trailing: SeasonTrailingKind;
}

/**
 * Game Pass theme — the bag of named colours + asset URLs admins author in PAM
 * and players consume as their UI theme (gamepass#252 / gamepass-dal#49).
 *
 * The bare document is what the public CDN copy (`theme.json`) serves; the PAM
 * admin API wraps it in a `{ success, data }` envelope and adds `published`
 * (whether the CDN copy was refreshed) on the PATCH response.
 *
 * Every key inside `colors` / `assets` is a free-form, FE-owned string — the
 * backend stores whatever we send, so both maps stay open (`Record<string,…>`)
 * rather than a fixed key union.
 */
export interface GamePassThemeDocument {
  colors?: Record<string, string>;
  assets?: Record<string, string>;
  updated_at?: string | null;
}

/** The theme as it appears inside the PAM admin envelope's `data`. */
export interface GamePassAdminTheme extends GamePassThemeDocument {
  /** PATCH only: whether the CDN copy was refreshed. `false` means the DB
   *  write succeeded but the public copy is stale — the caller should surface a
   *  "saved, not yet published; save again to retry" notice. */
  published?: boolean;
}

/** PAM admin theme envelope for both GET and PATCH: `{ success, data }`. */
export interface GamePassAdminThemeResponse {
  success?: boolean;
  data?: GamePassAdminTheme;
}

/**
 * Merge-patch body for `PATCH /gamepass/v2/admin/theme`.
 *
 * Only dirty keys are sent. Per key: a string sets/overwrites it, `null`
 * removes it (reset to default), an absent key is left untouched. Omitting a
 * whole section leaves that section untouched; an empty body is a no-op.
 */
export interface GamePassThemePatchRequest {
  colors?: Record<string, string | null>;
  assets?: Record<string, string | null>;
}

// ─── Monthly Challenges (Campaigns) API contract ──────────────────────────
//
// A Monthly Challenge is authored as a **Campaign** container
// (`/gamepass/v2/admin/campaigns`) that holds challenge groups + tiered tasks
// — the same challenge / task contract the Seasons flow already uses. A
// campaign either pins to a season's window (`duration_mode: "SEASON"` via
// `linked_season_id`) or runs on its own dates (`duration_mode: "FIXED"`).
//
// PROVISIONAL (FE-2319): field names and the status enum follow the backend
// Monthly Challenges wiring guide and mirror the Seasons shapes. Confirm
// against the admin section of `player-api.yaml` before relying on any field
// the create / list mappers don't yet read. Anything unconfirmed is kept
// optional / nullable so a partial payload never breaks a consumer — the same
// convention `SeasonApiRecord` uses above.

/** Canonical lifecycle status enum for a campaign. Mirrors the subset of
 *  `SEASON_API_STATUS` the campaign lifecycle exposes (create → draft;
 *  schedule/unschedule; activate; complete; cancel). Kept as a
 *  `const … as const` object for the same reasons as `SEASON_API_STATUS`
 *  (tree-shaking, typo-safety, string-literal union). Widen only as a
 *  coordinated BE + FE change. */
export const CAMPAIGN_API_STATUS = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

/** String-literal union derived from `CAMPAIGN_API_STATUS`. */
export type CampaignApiStatus =
  (typeof CAMPAIGN_API_STATUS)[keyof typeof CAMPAIGN_API_STATUS];

/** How a campaign's active window is determined:
 *  - `FIXED` — the campaign carries its own `start_date` / `end_date`.
 *  - `SEASON` — the campaign pins to a season's window via
 *    `linked_season_id` (the "start after this season" chaining the monthly
 *    Schedule step offers). */
export const CAMPAIGN_DURATION_MODE = {
  FIXED: "FIXED",
  SEASON: "SEASON",
} as const;

export type CampaignDurationMode =
  (typeof CAMPAIGN_DURATION_MODE)[keyof typeof CAMPAIGN_DURATION_MODE];

/** Wallet currency a campaign's rewards pay out in. PROVISIONAL — the wiring
 *  guide lists `rebet_cash` / `rebet_coin` (note singular `rebet_coin`); XP is
 *  carried separately as `xp_reward` on each tier, not as a currency. */
export type CampaignRewardCurrency = "rebet_cash" | "rebet_coin";

/** Per-campaign record returned by the campaign endpoints. Field names mirror
 *  the wiring guide; required-shaped fields are the ones the listing card
 *  needs (`name`, `status`), and everything else is optional / nullable to
 *  match partial lifecycle payloads (a `draft` has no `activated_at`, a
 *  `FIXED` campaign has no `linked_season_id`, etc.). */
export interface CampaignApiRecord {
  /** Unique id used as the React key and to load the Monthly Details page from
   *  its route param (currently `:challengeId` — named before this campaign
   *  wiring landed; it carries the campaign id). The create response returns
   *  `campaign_id`; the mapper should accept `id` as an alias so a future BE
   *  rename is free. */
  id?: string;
  campaign_id?: string;
  operator_id?: string;
  /** Internal / canonical name authored in the wizard's Overview step. */
  name: string;
  /** Admin-supplied user-facing override; falls back to `name` when null. */
  display_name?: string | null;
  description?: string | null;
  status: CampaignApiStatus;
  duration_mode?: CampaignDurationMode;
  /** ISO-8601. Present for `FIXED` campaigns; for `SEASON` campaigns it may be
   *  derived from the linked season, so it is optional / nullable here. */
  start_date?: string | null;
  end_date?: string | null;
  /** Season this campaign chains after / pins to (`duration_mode: SEASON`). */
  linked_season_id?: string | null;
  /** Whether players are auto-enrolled on activation (vs. an explicit player
   *  enroll call). */
  auto_enroll?: boolean;
  reward_currency?: CampaignRewardCurrency;
  /** Login-streak daily window boundary as a UTC hour (0–23). Authored on
   *  campaign create/update (FE-2668). */
  streak_reset_hour?: number | null;
  /** Total challenge groups / tasks configured — drives the listing card
   *  metrics, same as the Seasons card. */
  challenge_count?: number;
  task_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
  scheduled_at?: string | null;
  activated_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
}

/** Body for `POST /gamepass/v2/admin/campaigns`. `operator_id` is appended in
 *  the query layer from `GET /config` (same as `CreateSeasonRequest`), so it
 *  is intentionally absent here. Dates are optional because a `SEASON`-mode
 *  campaign may inherit them from its linked season. */
export interface CreateCampaignRequest {
  name: string;
  description?: string;
  duration_mode: CampaignDurationMode;
  start_date?: string;
  end_date?: string;
  linked_season_id?: string | null;
  auto_enroll?: boolean;
  reward_currency?: CampaignRewardCurrency;
  /** Login-streak daily window boundary as a UTC hour (0–23). */
  streak_reset_hour?: number;
}

/** Draft campaign returned from `POST /gamepass/v2/admin/campaigns`. Early
 *  drafts may not have their window populated yet, matching
 *  `CreatedSeasonRecord`. */
export interface CreatedCampaignRecord
  extends Omit<CampaignApiRecord, "start_date" | "end_date"> {
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateCampaignResponse {
  success: boolean;
  campaign?: CreatedCampaignRecord;
  data?: CreatedCampaignRecord;
}

/** Partial body for `PUT /gamepass/v2/admin/campaigns/{campaign_id}` — only
 *  changed fields are sent (mirrors `UpdateSeasonRequest`). */
export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  duration_mode?: CampaignDurationMode;
  start_date?: string;
  end_date?: string;
  linked_season_id?: string | null;
  auto_enroll?: boolean;
  reward_currency?: CampaignRewardCurrency;
  /** Login-streak daily window boundary as a UTC hour (0–23). `null` clears a
   *  previously stamped hour when the campaign no longer has a Login Streak
   *  task. */
  streak_reset_hour?: number | null;
}

/** Shared envelope for every campaign lifecycle response (create-update,
 *  schedule, unschedule, activate, complete, cancel). The BE echoes the
 *  updated record so the listing / detail caches can refresh in place — same
 *  shape as the Season lifecycle envelopes. */
export interface CampaignMutationResponse {
  success: boolean;
  campaign?: CampaignApiRecord;
  data?: CampaignApiRecord;
}

/** Envelope for `DELETE /gamepass/v2/admin/campaigns/{campaign_id}`. The BE
 *  returns only a success flag (no echoed record) — mirrors
 *  `DeleteSeasonResponse` / `DeleteChallengeResponse` / `DeleteTaskResponse`. */
export interface DeleteCampaignResponse {
  success: boolean;
}

/** Optional filter for `GET /gamepass/v2/admin/campaigns`. */
export interface CampaignsListQuery {
  status?: CampaignApiStatus;
}

export interface CampaignsListResponse {
  success: boolean;
  campaigns: CampaignApiRecord[];
}

/** Body for creating a challenge group under a campaign. A Monthly Challenge
 *  reuses the season challenge/task contract with `campaign_id` in place of
 *  `season_id` (see the `CREATE_CHALLENGE` comment in `ApiRoutes.js`), so this
 *  mirrors `CreateChallengeRequest` minus the season-only fields: the monthly
 *  card carries no FREE/PREMIUM path, icon, or per-group data source, so
 *  `path_availability` / `data_source` / icon keys are intentionally absent.
 *  PROVISIONAL (FE-2322) — confirm the `campaign_id` field name and group shape
 *  against the admin OpenAPI before GA. */
export interface CreateCampaignChallengeRequest {
  campaign_id: string;
  name: string;
  /** Zero-based position of this group within the campaign, stamped from the
   *  card's position in the wizard's Challenges list. */
  sort_order: number;
  /** Admin-supplied free-text description. Only sent when non-empty (omitted
   *  for a blank field), matching the Season challenge mapping. */
  description?: string;
}

/** A single reward granted for clearing a tier. The BE nests reward payouts in
 *  a per-tier `rewards` array (a tier can grant more than one reward), keyed by
 *  the same `{ amount, currency }` wire shape the Season challenge rewards use.
 *  The live `/gamepass/v2/admin/tasks` validation constrains `currency` to
 *  `['free_pick', 'free_spin', 'play', 'profit_boost', 'value']`; the monthly
 *  reward pills only reach `play` (Rebet Coins / XP) and `value` (Rebet Cash)
 *  via `REWARD_TYPE_TO_CHALLENGE_CURRENCY`. */
export interface CampaignTierReward {
  amount: number;
  /** `play` / `value` for currency rewards; the bonus category (e.g.
   *  `free_pick`, `profit_boost`, `deposit_match`) for bonus rewards. The
   *  monthly task contract allowlists these alongside `free_spin`. */
  currency: ChallengeRewardCurrency | string;
  /** Bonus rewards only — the BE grant identifier (e.g. `free_cash_pick`). */
  reward_type?: string;
  /** Bonus rewards only — tunable params from `config.bonus_reward_types`. */
  params?: Record<string, string | number>;
}

/** One tier of a Monthly Challenge's tiered task: a threshold the player must
 *  reach and the reward(s) granted for reaching it. Unlike a Season (single
 *  goal + challenge-level rewards), a monthly task escalates through several
 *  tiers. The tier's position in the array carries its order (the BE rejects a
 *  standalone `tier` index), and the payout lives in the nested `rewards`
 *  array. Confirmed against the `/gamepass/v2/admin/tasks` 422 contract. */
export interface CampaignTaskTier {
  /** Count / amount the player must reach for this tier (follower count,
   *  number of qualifying picks, wager amount, …). */
  threshold: number;
  rewards: ReadonlyArray<CampaignTierReward>;
  /** Standalone per-tier XP bonus granted alongside the currency reward(s),
   *  distinct from the `rewards` payout. `0` when the admin left it blank. */
  xp_reward?: number;
  /** STREAK tasks: high-water (`streak_best`) has reached this rung. */
  unlocked?: boolean;
  claimed?: boolean;
}

/** Body for creating the tiered task under a campaign challenge group. Reuses
 *  the season task vocabulary (`data_source`, `rules`, `bet_type`, odds, min
 *  bet) with `campaign_id` in place of `season_id`, and adds the monthly-only
 *  `tiers` array. PROVISIONAL (FE-2322) — confirm the `campaign_id` field name,
 *  the `tiers` shape, and the REBETS data source against the admin OpenAPI
 *  before GA. */
export interface CreateCampaignTaskRequest {
  campaign_id: string;
  /** Parent challenge-group id. `null` before the group is persisted (the
   *  sequential create flow fills it once the group create returns), mirroring
   *  `CreateTaskRequest.challenge_id`. */
  challenge_id: string | null;
  /** Parent challenge-group display name. */
  challenge: string;
  /** Zero-based slot of this task within its group. Monthly groups hold a
   *  single tiered task, so this is `0` today. */
  challenge_order: number;
  name: string;
  description: string;
  data_source: TaskDataSource;
  /** SOCIAL-only. Login Streak ships `daily_login` (FE-2647); Follow / Referral
   *  still send their event types. `null` for Sportsbook / Casino. */
  event_type: TaskEventType | null;
  counting_method: string;
  /** Sportsbook pick-type column; `null` for the "any pick" default and for
   *  non-sportsbook tasks. */
  bet_type: TaskBetType | null;
  /** Sportsbook Minimum Odds (American); `null` when blank / non-sportsbook. */
  min_odds_american: number | null;
  /** Minimum qualifying wager; `null` when blank / not applicable. */
  min_bet_amount: number | null;
  /** Sportsbook action → outcome requirement; `null` for non-sportsbook. */
  outcome_required: TaskOutcomeRequired | null;
  rules: ReadonlyArray<TaskRule>;
  tiers: ReadonlyArray<CampaignTaskTier>;
  /** Login Streak only — UTC hour (0–23) the daily window resets. Omitted for
   *  every other monthly task type. */
  streak_reset_hour?: number;
}

/** Minimal created challenge-group record echoed by the campaign challenge
 *  create. Only the id fields matter to the orchestration flow (the task
 *  create needs the group's id); the BE may return `id` or `challenge_id`.
 *  PROVISIONAL (FE-2323) — confirm the field names against the admin OpenAPI. */
export interface CreatedCampaignChallengeRecord {
  id?: string;
  challenge_id?: string;
  campaign_id?: string;
}

export interface CreateCampaignChallengeResponse {
  success: boolean;
  challenge?: CreatedCampaignChallengeRecord;
  data?: CreatedCampaignChallengeRecord;
}

/** Minimal created task record echoed by the campaign task create.
 *  PROVISIONAL (FE-2323) — confirm the field names against the admin OpenAPI. */
export interface CreatedCampaignTaskRecord {
  id?: string;
  task_id?: string;
  campaign_id?: string;
  challenge_id?: string | null;
}

export interface CreateCampaignTaskResponse {
  success: boolean;
  task?: CreatedCampaignTaskRecord;
  data?: CreatedCampaignTaskRecord;
}

/** A campaign task as returned by `GET /gamepass/v2/admin/tasks?campaign_id=…`.
 *  The BE reuses the season task listing shape ({@link TaskListRecord}) and
 *  adds the monthly-only escalating `tiers` array. Confirmed (FE-2324,
 *  2026-07-24) against a live test03 response: `tiers` echoes the create shape
 *  ({ threshold, rewards: [{ amount, currency }], xp_reward }); the per-tier
 *  `xp_reward` re-hydrates into the tier editor (FE-2427). */
export interface CampaignTaskListRecord extends TaskListRecord {
  campaign_id?: string | null;
  tiers?: ReadonlyArray<CampaignTaskTier> | null;
  /** Login Streak — UTC hour (0–23) echoed from the parent campaign. */
  streak_reset_hour?: number | string | null;
}

/** Campaign-scoped tasks list envelope — `GET …/tasks?campaign_id=…`. Narrows
 *  {@link TasksListResponse} to {@link CampaignTaskListRecord} so callers read
 *  `tiers` / `campaign_id` off `tasks` without an `as` downcast. */
export type CampaignTasksListResponse =
  TasksListResponse<CampaignTaskListRecord>;
